import { faRepeat } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createStyles,
  Link,
  makeStyles,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { DragIndicator } from '@material-ui/icons';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import VoxelIconButton from '../../../../../components/voxel/Buttons/VoxelIconButton';
import VoxelLink from '../../../../../components/voxel/Link/VoxelLink';
import VoxelText from '../../../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../../../components/voxel/Tooltip/VoxelTooltipShort';
import { ContentActionGq } from '../../../../../graphql/projects/models/ContentActionGq';
import { ContentKeywordGq } from '../../../../../graphql/projects/models/ContentKeywordGq';
import { ContentKeywordSelecting } from '../../../../../graphql/projects/models/ContentKeywordSelectingGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { TagGroupGq } from '../../../../../graphql/projects/models/TagGroupGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { DisplaySerpResult } from '../../../../DataManagementPage/components/Dialogs/SerpResultsDialog';
import { getTaskUrl } from '../ContentTaskTable/TaskUrl';
import KeywordAvatar from './KeywordAvatar';
import RowCheckbox from './RowCheckbox';
import TableCellCheckIcon from './TableCellCheckIcon';

export interface ContentKeywordTableRowProps {
  contentKeyword: ContentKeywordGq;
  project: ProjectGq;
  selectNewUrl: (url: string) => void;
  clientUrl?: string;
  contentTaskUrl?: string;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  isDragginGlobal?: boolean;
  selectionState: ContentKeywordSelecting;
  isSelected: boolean;
  onMouseDown?: (contentKeywordId: number, index: number) => void;
  onMouseUp?: () => void;
  onMouseOver?: (index: number) => void;
  index: number;
  setDisplaySerpResult: (state: DisplaySerpResult) => void;
  showTags?: TagGroupGq[];
  hideTags: boolean;
}
const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: {
      cursor: 'pointer',
      userSelect: 'none',
    },
    selectMode: {
      backgroundColor: theme.palette.grey[100],
    },
    tableCellThirty: {
      width: '30%',
    },
    tableCellTen: {
      width: '10%',
    },
    dragging: {
      display: 'table',
    },
    naText: {
      color: theme.palette.grey[400],
    },
    urlText: {
      color: theme.palette.text.secondary,
    },
  })
);

const ContentKeywordTableRow: React.FC<ContentKeywordTableRowProps> = ({
  contentKeyword,
  selectNewUrl,
  contentTaskUrl,
  provided,
  clientUrl,
  snapshot,
  isDragginGlobal,
  selectionState,
  isSelected,
  onMouseDown,
  onMouseOver,
  onMouseUp,
  index,
  setDisplaySerpResult,
  showTags,
  hideTags,
  project,
}) => {
  // Styling
  const theme = useTheme();
  const classes = useClasses(theme);

  const isCreateAction = !!(contentKeyword.taskAction === ContentActionGq.CREATE);
  const bestUrlFull = contentKeyword.bestSerpUrl ?? contentKeyword.bestDomainSerpUrl;
  const bestUrl = getTaskUrl(bestUrlFull, clientUrl);
  const taskHasBestUrl = contentTaskUrl === bestUrlFull || bestUrl == contentTaskUrl;
  const tags = useMemo(
    () =>
      showTags
        .map((tagGroup) => tagGroup.tags.find((tg) => contentKeyword.tags?.includes(tg.id))?.name)
        .filter((tgName) => !!tgName?.length)
        .join(' / '),
    [contentKeyword.id]
  );

  return (
    <TableRow
      component="div"
      key={contentKeyword.id}
      onMouseDown={onMouseDown ? () => onMouseDown(contentKeyword.id, index) : undefined}
      onMouseOver={onMouseOver ? () => onMouseOver(index) : undefined}
      onMouseUp={onMouseUp}
      hover={!isSelected && !isDragginGlobal}
      selected={isSelected}
      className={clsx({
        [classes.tableRow]: true,
        [classes.selectMode]: false,
        [classes.dragging]: snapshot.isDragging,
      })}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <TableCell padding={'none'} component="div">
        <div className={'flex flex-row items-center'}>
          <div {...provided.dragHandleProps}>
            <DragIndicator
              fontSize={'small'}
              color={'disabled'}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onMouseUp={(event) => event.stopPropagation()}
              className={'cursor-grab mt-2'}
            />
          </div>
          <div className="pl-2">
            <KeywordAvatar index={index} />
          </div>
        </div>
      </TableCell>

      <TableCell padding={'checkbox'} component="div">
        <RowCheckbox contentKeyword={contentKeyword} selectionState={selectionState} />
      </TableCell>

      <TableCell className={!isCreateAction ? classes.tableCellThirty : ''} component="div">
        <Typography variant={'body2'}>{contentKeyword.keyword}</Typography>
      </TableCell>

      {project.hasEnglishTranslation && (
        <TableCell component="div">
          <Typography variant={'body2'}>{contentKeyword.keywordEnglish}</Typography>
        </TableCell>
      )}

      <TableCell
        component="div"
        className={!isCreateAction ? classes.tableCellTen : ''}
        align={'right'}
      >
        {formatNumber(contentKeyword.volume)}
      </TableCell>

      {!!showTags?.length && <TableCell>{hideTags ? null : tags}</TableCell>}

      <TableCellCheckIcon
        component="div"
        checked={contentKeyword.inTitle}
        contentAction={contentKeyword.taskAction}
      />

      <TableCellCheckIcon
        component="div"
        checked={contentKeyword.inH1}
        contentAction={contentKeyword.taskAction}
      />

      <TableCellCheckIcon
        component="div"
        checked={contentKeyword.inContent}
        contentAction={contentKeyword.taskAction}
      />

      <TableCell component="div" className={classes.tableCellTen} align={'right'}>
        {isCreateAction ? (
          <Typography variant={'body2'} className={classes.naText}>
            N/A
          </Typography>
        ) : (
          <Link
            component={'button'}
            variant={'body2'}
            color={'textPrimary'}
            underline="hover"
            onClick={(event: React.MouseEvent<any>) => {
              event.stopPropagation();
              setDisplaySerpResult({
                keywordId: contentKeyword.keywordId,
                rankNumber: contentKeyword?.rank,
              });
            }}
            onMouseDown={(event: React.MouseEvent<any>) => event.stopPropagation()}
          >
            {contentKeyword?.rank ?? '100+'}
          </Link>
        )}
      </TableCell>

      <TableCell component="div" align={'right'}>
        {!taskHasBestUrl && (
          <Link
            component={'button'}
            variant={'body2'}
            underline="hover"
            onClick={(event: React.MouseEvent<any>) => {
              event.stopPropagation();
              setDisplaySerpResult({
                keywordId: contentKeyword.keywordId,
                rankNumber: contentKeyword.bestSerpRank,
              });
            }}
            onMouseDown={(event: React.MouseEvent<any>) => {
              event.stopPropagation();
            }}
          >
            {contentKeyword.bestSerpRank
              ? contentKeyword.bestSerpRank
              : contentKeyword.bestDomainSerpRank
                ? '+100'
                : 'N/A'}
          </Link>
        )}
      </TableCell>
      <TableCell
        component="div"
        align={'left'}
        className={`
          ${taskHasBestUrl && classes.urlText}
          ${!bestUrlFull && classes.naText}
        `}
      >
        <div className={'flex flex-row gap-2 flex-nowrap items-center'}>
          {!taskHasBestUrl && (
            <div>
              <VoxelTooltipShort
                disabled={taskHasBestUrl || !bestUrlFull?.length}
                message="Switch current to this url"
              >
                <VoxelIconButton
                  color="white"
                  disabled={taskHasBestUrl || !bestUrlFull?.length}
                  size="xxs"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    selectNewUrl(bestUrlFull ?? '');
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  icon={<FontAwesomeIcon icon={faRepeat} size="sm" />}
                />
              </VoxelTooltipShort>
            </div>
          )}
          {!taskHasBestUrl && (
            <div className="max-w-[128px]">
              <VoxelText variant="form-caption" ellipsis>
                {!bestUrl?.length && 'N/A'}
                {!!bestUrl?.length && (
                  <VoxelLink
                    href={bestUrlFull}
                    openInNewTab
                    onMouseDown={(event: React.MouseEvent<any>) => {
                      event.stopPropagation();
                    }}
                  >
                    {bestUrl?.slice(0, 40)}
                    {bestUrl?.length > 41 ? '...' : ''}
                  </VoxelLink>
                )}
              </VoxelText>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default ContentKeywordTableRow;
